import {
  DEFAULT_AXIS_LABEL_FONT_SIZE,
  SELECTABLE_Y_AXIS_POINTER_LABEL_MARGIN
} from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import { YAXisOption } from "echarts/types/dist/shared";
import { createEntityState } from "entities/charts/factory";
import { ChartOption } from "entities/charts/options";
import { EvaChartStates } from "types/factory";
import { isLessThanSampleSizeLabel } from "utils/groupBy/isLessThanSampleSizeLabel";
import { isOthersGroupLabel } from "utils/groupBy/isOthersGroupLabel";

import { createChartLocks } from "../locks";
import { DEFAULT_Z_INDEX, HIGHLIGHTED_Z_INDEX, SELECTED_WELL_Z_INDEX } from "./constants";
import { dropOthersFromStackedBar } from "./hideOthers";
import { recentCategoryLabelWidth } from "./rigFracRecentActivity";
import { updateOptionsWithTypeWellsSeries } from "./updateOptionsWithTypeWellSeries";
import {
  computeSelectionStates,
  formatChartTitleWithSubscript,
  getStackedBarChartMinMax,
  getStackedBarPositions
} from "./utils";

// Wider than Recent Activity's RECENT_LABEL_MIN/MAX_WIDTH (56/150) — this chart
// has a full plot area to work with, not a small dashboard widget's fixed gutter.
const CATEGORY_LABEL_MIN_WIDTH = 80;
const CATEGORY_LABEL_MAX_WIDTH = 260;

/**
 * Same stacked-bar data/series shape as StackedBar, rotated: the category
 * (bucket) axis is y (inverse, so the top-ranked bucket draws first) and the
 * numeric axis is x. chartTypeParam's roles are swapped for this reason — see
 * features/stackedBarHorizontal.ts and proxies.ts, which swaps them back
 * before hitting the wire (the backend's response shape doesn't change).
 */
export function translateStackedBarHorizontalResponseToOptions(
  states: Partial<EvaChartStates>
) {
  const {
    title,
    typeWellSeries = [],
    hoverLegendItem,
    hoverLegendGroup,
    attentionWells,
    chartType,
    xAxisFieldSync,
    xAxisSort,
    focusLegendItems,
    chartTypeParam,
    groupBy,
    splitYearApplied,
    hideOthers,
    histogramToggle
  } = states;

  // EVA-306: drop Others first, so min/max and the position-total sort both
  // recompute from the remaining bars. See stackedBar.ts for the same pattern.
  let { layout, series } = states;
  if (hideOthers?.active) {
    ({ series, layout } = dropOthersFromStackedBar(series, layout));
  }

  const categoryAxisTitle = layout?.xAxis?.title || "";
  const { yMin: valueMin, yMax: valueMax } = getStackedBarChartMinMax(series);

  const { lockedXMin, lockedXMax } = createChartLocks({
    type: EvaChart.StackedBarHorizontal,
    states
  });

  // Wider bounds than Recent Activity's compact widget: category labels here
  // are whatever field the user's focus/group-by is set to, which often runs
  // longer than a dashboard widget's short group names.
  const categoryLabelWidth = recentCategoryLabelWidth(
    (layout?.xAxis?.data ?? []).map((label) => String(label)),
    DEFAULT_AXIS_LABEL_FONT_SIZE,
    CATEGORY_LABEL_MIN_WIDTH,
    CATEGORY_LABEL_MAX_WIDTH
  );

  let options = createEntityState(ChartOption.Chart, {
    chartType,
    title: {
      ...createEntityState(ChartOption.Title, {
        text: formatChartTitleWithSubscript(title)
      })
    },
    xAxis: {
      ...createEntityState(ChartOption.XAxis, {
        name: formatChartTitleWithSubscript(layout.yAxis.title),
        nameGap: 27,
        min: lockedXMin,
        max: lockedXMax,
        // Explicit (on — matches a value axis's default gridlines) so the
        // screenshot X-Axis Major toggle has a real boolean to flip rather
        // than being a no-op (same reasoning as rigFracRecentActivity.ts).
        splitLine: { show: true, lineStyle: { color: "#D9D9D9" } },
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        lock: {
          show: true,
          min: {
            value: Math.floor(valueMin.value),
            type: valueMin.type
          },
          max: {
            value: Math.floor(valueMax.value),
            type: valueMax.type,
            override: true
          }
        },
        axisPointer: {
          label: {
            margin: SELECTABLE_Y_AXIS_POINTER_LABEL_MARGIN
          }
        }
      })
    },
    // yAxis is set below, once categoryData (depends on sort/axis-sync logic
    // further down) is known. This grid.left and the yAxis nameGap below are
    // just a pre-layout starting point — applySpacingToChartOptions'
    // getYAxisNameGap re-derives both from the widest rendered label, capped at
    // axisLabel.width.
    grid: { top: 46, bottom: 46, left: categoryLabelWidth + 32, right: 20 },
    series: series.map((location, index) => {
      const { label, style, y, groupTitle } = location;

      const data = y;
      const id =
        groupTitle !== label
          ? `${groupTitle}, ${label}, ${index}`
          : `${groupTitle}, ${index}`;
      const { isSelectedWell, isHovered } = computeSelectionStates({
        hoverLegendItem,
        hoverLegendGroup,
        attentionWells,
        label,
        groupTitle
      });
      const barSeries = createEntityState(ChartOption.Bar, {
        id,
        name: label,
        data: data,
        itemStyle: { color: style?.hexColor ?? "#000" },
        stack: "stackedBar",
        z: isSelectedWell
          ? SELECTED_WELL_Z_INDEX
          : isHovered
          ? HIGHLIGHTED_Z_INDEX
          : DEFAULT_Z_INDEX
      });

      return barSeries;
    })
  });

  const sortedSeries = Array.isArray(options.series) ? [...options.series] : [];
  // Mirrors StackedBar's axis-sync check, against the category field — which
  // for this chart is chartTypeParam.y, not .x.
  const isSameGroupBy =
    !!groupBy?.property && groupBy.property === chartTypeParam?.properties?.y?.property;

  const isAxisSync =
    !splitYearApplied &&
    xAxisFieldSync?.active &&
    focusLegendItems &&
    focusLegendItems.length > 0 &&
    isSameGroupBy;

  let categoryData: string[];
  let indexMapping: number[];

  if (isAxisSync) {
    // EVA-306: this rebuilds the category axis from the focus legend, not from
    // layout.xAxis.data — dropOthersFromStackedBar above never touches this list,
    // so Others has to be filtered out here too or it reappears on a synced axis.
    const filteredLegendItems = focusLegendItems.filter(
      (item) =>
        item.inFilter !== false &&
        !(
          hideOthers?.active &&
          (isOthersGroupLabel(item.title) || isLessThanSampleSizeLabel(item.title))
        )
    );
    const legendOrder = filteredLegendItems.map((item) => item.title);

    categoryData = legendOrder;
    indexMapping = legendOrder.map((newLabel) =>
      layout.xAxis.data.findIndex((originalLabel) => originalLabel === newLabel)
    );

    sortedSeries.sort((a, b) => {
      const aIndex = legendOrder.indexOf(String(a.name));
      const bIndex = legendOrder.indexOf(String(b.name));
      if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return 0;
    });
  } else {
    const positions = getStackedBarPositions(
      layout.xAxis.data,
      sortedSeries,
      xAxisSort.properties?.value,
      histogramToggle?.active ? null : chartTypeParam?.properties?.y?.bin?.MaxBins,
      layout.xAxis.maxBinsSortValues
    );

    categoryData = positions.map((item) => String(item.label));
    indexMapping = positions.map((item) => item.index);
  }

  sortedSeries.forEach((series) => {
    if (Array.isArray(series.data)) {
      const originalData = [...series.data];
      series.data = indexMapping.map((originalIndex) =>
        originalIndex === -1 ? null : originalData[originalIndex]
      );
    }
  });

  options.yAxis = {
    ...(createEntityState(ChartOption.XAxisCategory, {
      id: "yAxis",
      name: formatChartTitleWithSubscript(categoryAxisTitle),
      nameGap: categoryLabelWidth + 16,
      inverse: true,
      // Explicit (off — a category axis defaults to no gridlines) so the
      // screenshot Y-Axis Major toggle has a real boolean to flip rather
      // than being a no-op (same reasoning as rigFracRecentActivity.ts).
      splitLine: { show: false },
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      useY: false,
      silent: true,
      data: categoryData,
      axisLabel: {
        width: categoryLabelWidth,
        overflow: "truncate",
        // DEFAULT_X_AXIS_CATEGORY_OPTIONS.axisLabel.padding is tuned for a
        // bottom x-axis and offsets labels on this y-axis — reset it since we
        // reuse the XAxisCategory generator instead of a plain literal.
        padding: [0, 0, 0, 0]
      }
    }) as unknown as YAXisOption)
  };

  options.legend = {
    ...((options.legend as object) || {}),
    data: sortedSeries.map((series) => ({
      name: series.name as string,
      itemStyle: {
        color: isAxisSync
          ? focusLegendItems.find((item) => item.title === series.name)?.color
          : // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (series as any).itemStyle?.color || "#000"
      }
    })),
    show: false
  };

  options.series = sortedSeries;

  if (typeWellSeries.length > 0) {
    options = updateOptionsWithTypeWellsSeries(options, typeWellSeries);
  }
  if (series.length === 0 && typeWellSeries.length === 0) {
    // The Hide Others redraw uses replaceMerge "series", which keeps the old
    // category axis unless it is cleared explicitly; both axes are needed so a
    // notMerge rebuild of this shape doesn't throw.
    options =
      hideOthers?.active && states.series?.length > 0
        ? { xAxis: { type: "value" }, yAxis: { type: "category", data: [] }, series: [] }
        : {};
  }
  return options;
}
